import { useState } from "react";
import { toast } from "react-hot-toast";
import { useParams } from "react-router-dom";

import { Button, Modal, ModalHeader } from "../../components/ui";
import {
  Empty,
  ErrorState,
  Loading,
} from "../../components/WorkspacePrimitives";
import {
  useAgentCompanies,
  useAgentConfiguration,
  useAgentDocuments,
  useUploadAgentInput,
} from "../api/e2e";
import AgentDocumentCatalog, {
  DOCUMENT_SORTS,
  type DocumentSort,
  type DocumentTypeFilter,
} from "../components/AgentDocumentCatalog";
import AgentDocumentUploadForm from "../components/AgentDocumentUploadForm";
import CompanyWorkspacePage from "../components/CompanyWorkspacePage";
import { validateAgentInputFile } from "../lib/inputFiles";

export default function AgentCompanyDocumentsPage() {
  const { companyId = "" } = useParams<{ companyId: string }>();
  const companies = useAgentCompanies();
  const configuration = useAgentConfiguration();
  const [search, setSearch] = useState("");
  const [documentType, setDocumentType] = useState<DocumentTypeFilter>("all");
  const [sort, setSort] = useState<DocumentSort>("release_desc");
  const selectedSort = DOCUMENT_SORTS[sort];
  const documents = useAgentDocuments(companyId, {
    search,
    documentType: documentType === "all" ? undefined : documentType,
    ...selectedSort,
  });
  const upload = useUploadAgentInput();
  const company = companies.data?.find((item) => item.id === companyId);
  const [formVersion, setFormVersion] = useState(0);
  const [file, setFile] = useState<File | null>(null);
  const [description, setDescription] = useState("");
  const [uploadingDocument, setUploadingDocument] = useState(false);

  const resetUploadForm = () => {
    setFile(null);
    setDescription("");
    setFormVersion((current) => current + 1);
  };

  const closeUploadModal = () => {
    if (upload.isPending) return;
    resetUploadForm();
    setUploadingDocument(false);
  };

  if (
    (companies.isPending && !companies.data) ||
    (configuration.isPending && !configuration.data) ||
    (documents.isPending && !documents.data)
  ) {
    return <Loading label="Loading company documents…" />;
  }
  if (companies.error && !companies.data) {
    return (
      <ErrorState error={companies.error} onRetry={() => companies.refetch()} />
    );
  }
  if (configuration.error && !configuration.data) {
    return (
      <ErrorState
        error={configuration.error}
        onRetry={() => configuration.refetch()}
      />
    );
  }
  if (documents.error && !documents.data) {
    return (
      <ErrorState error={documents.error} onRetry={() => documents.refetch()} />
    );
  }
  if (!company) return <Empty>This company is not available.</Empty>;
  const config = configuration.data;
  if (!config) return null;
  const uploadDocument = async () => {
    if (!file) {
      toast.error("Choose a file to upload.");
      return;
    }
    const problem = validateAgentInputFile(file, "other_document");
    if (problem) {
      toast.error(problem);
      return;
    }
    try {
      const result = await upload.mutateAsync({
        companyId,
        file,
        role: "other_document",
        metadata: { description },
      });
      if (result.state !== "ready") {
        throw new Error(
          result.rejection_reason ?? `${file.name} was rejected after upload.`,
        );
      }
      toast.success(`${file.name} added to the document catalog.`);
      resetUploadForm();
      setUploadingDocument(false);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Upload failed.");
    }
  };

  return (
    <>
      <CompanyWorkspacePage companies={companies.data ?? []} company={company}>
        <section className="company-tab-content" aria-label="Company documents">
          <AgentDocumentCatalog
            companyId={companyId}
            documents={documents.data ?? []}
            isFetching={documents.isFetching}
            search={search}
            onSearchChange={setSearch}
            documentType={documentType}
            onDocumentTypeChange={setDocumentType}
            sort={sort}
            onSortChange={setSort}
            emptyMessage="No source documents have been captured for this company."
            actions={
              <Button tone="primary" onClick={() => setUploadingDocument(true)}>
                Upload document
              </Button>
            }
          />
        </section>
      </CompanyWorkspacePage>
      {uploadingDocument && (
        <Modal
          ariaLabelledBy="upload-document-title"
          dismissible={!upload.isPending}
          onClose={closeUploadModal}
        >
          <ModalHeader
            eyebrow="Document catalog"
            title="Upload document"
            titleId="upload-document-title"
            closeDisabled={upload.isPending}
            onClose={closeUploadModal}
          />
          <AgentDocumentUploadForm
            key={formVersion}
            file={file}
            description={description}
            accept={config.supported_extensions.join(",")}
            busy={upload.isPending}
            showIntro={false}
            onFileChange={setFile}
            onDescriptionChange={setDescription}
            onCancel={closeUploadModal}
            onSubmit={() => void uploadDocument()}
          />
        </Modal>
      )}
    </>
  );
}
